/**
 * TopicDetailPanel — story detail card (bd deepcontext-b3to.8).
 *
 * Renders one story's full detail: headline/event, entity chips, domain
 * chips, first/last seen, and the member list (domain, title, similarity).
 * Pure presentational — the host (`ClustersGraph`) owns fetching via
 * `GET /api/clusters/topics/:id` and passes loading/error/detail down, and
 * renders this inside a floating card anchored to the clicked bubble
 * (deepcontext-rlvt) rather than a fixed side panel.
 */
import React from 'react';
import {Button} from '../../components/ui/button';
import type {ClustersTopicDetail} from '../../api/clusters';

interface TopicDetailPanelProps {
    loading: boolean;
    error: string | null;
    detail: ClustersTopicDetail | null;
    onClose: () => void;
}

function formatSeen(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-US', {day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'});
}

const TopicDetailPanel: React.FC<TopicDetailPanelProps> = ({loading, error, detail, onClose}) => {
    return (
        <div
            data-testid="topic-detail-panel"
            className="w-full max-w-full bg-surface border border-line rounded-xl shadow-lg px-4 py-3 space-y-2.5"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                        Story
                    </p>
                    <h2
                        data-testid="topic-detail-heading"
                        className="text-sm font-semibold text-ink"
                    >
                        {loading ? 'Loading…' : detail?.headline ?? detail?.event ?? '—'}
                    </h2>
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={onClose}
                    aria-label="Close story detail panel"
                >
                    ×
                </Button>
            </div>

            {loading && (
                <p data-testid="topic-detail-loading" className="text-xs text-ink-muted">
                    Loading story detail…
                </p>
            )}

            {!loading && error && (
                <p data-testid="topic-detail-error" className="text-xs text-risk">
                    {error}
                </p>
            )}

            {!loading && !error && detail && (
                <>
                    {detail.event && detail.headline && detail.event !== detail.headline && (
                        <p className="text-xs text-ink-muted">{detail.event}</p>
                    )}

                    {detail.entities.length > 0 && (
                        <div data-testid="topic-detail-entities" className="flex flex-wrap gap-1.5">
                            {detail.entities.map((e) => (
                                <span
                                    key={e.name}
                                    className="text-xs font-semibold rounded-full px-2.5 py-0.5 bg-nav-active text-accent-active"
                                >
                                    {e.name}
                                </span>
                            ))}
                        </div>
                    )}

                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-ink-muted mb-1">
                            Domains · {detail.domains.length}
                        </p>
                        {detail.domains.length === 0 ? (
                            <p className="text-xs text-ink-muted">—</p>
                        ) : (
                            <div data-testid="topic-detail-domains" className="flex flex-wrap gap-1.5">
                                {detail.domains.map((d) => (
                                    <span
                                        key={d}
                                        className="text-xs rounded-full px-2.5 py-0.5 bg-ghost-bg text-ink-muted"
                                    >
                                        {d}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <dl className="grid grid-cols-2 gap-2">
                        <div className="bg-canvas rounded-lg px-2.5 py-1.5">
                            <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-muted">
                                First seen
                            </dt>
                            <dd className="text-xs font-semibold text-ink">
                                {formatSeen(detail.first_seen)}
                            </dd>
                        </div>
                        <div className="bg-canvas rounded-lg px-2.5 py-1.5">
                            <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-muted">
                                Last seen
                            </dt>
                            <dd className="text-xs font-semibold text-ink">
                                {formatSeen(detail.last_seen)}
                            </dd>
                        </div>
                    </dl>

                    <p className="text-[10px] font-bold uppercase tracking-wide text-ink-muted">
                        Members · {detail.members.length}
                    </p>
                    {detail.members.length === 0 ? (
                        <p data-testid="topic-detail-empty" className="text-xs text-ink-muted">
                            No members yet.
                        </p>
                    ) : (
                        <ul
                            data-testid="topic-detail-members"
                            className="space-y-1.5 text-xs max-h-48 overflow-y-auto"
                        >
                            {detail.members.map((m) => (
                                <li key={m.url_hash} className="flex items-center gap-2">
                                    <span className="text-ink-muted shrink-0">{m.site_domain}</span>
                                    {m.url ? (
                                        <a
                                            href={m.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-accent-active font-semibold hover:underline truncate"
                                            title={m.url}
                                        >
                                            {m.title || m.url}
                                        </a>
                                    ) : (
                                        <span className="font-mono text-ink truncate">
                                            {m.title || m.url_hash.slice(0, 16) + '…'}
                                        </span>
                                    )}
                                    <span className="ml-auto font-extrabold text-ink shrink-0">
                                        {m.sim_event != null ? `${(m.sim_event * 100).toFixed(0)}%` : '—'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
};

export default TopicDetailPanel;
